<template>
  <a-modal
    :open="visible"
    :confirm-loading="loading"
    :footer="null"
    width="600px"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">
        <i :class="['fa-solid', productIcon]" />
        {{ product?.name }} 설정 수정
      </div>
    </template>

    <div class="modal-content">
      <a-form layout="vertical">
        <!-- 제품명 (읽기 전용) -->
        <a-form-item label="제품명">
          <a-input :value="product?.name" disabled />
        </a-form-item>

        <!-- 설치비 -->
        <a-form-item label="설치비 (VAT 포함)">
          <a-input-number
            v-model:value="form.installationFee"
            :min="0"
            :formatter="value => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
            :parser="value => value.replace(/₩\s?|(,*)/g, '')"
            style="width: 100%"
          />
        </a-form-item>

        <!-- 사용료 섹션 -->
        <a-divider>사용료 (VAT 포함)</a-divider>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="월간 사용료">
              <a-input-number
                v-model:value="form.usageFee.monthly"
                :min="0"
                :formatter="value => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="value => value.replace(/₩\s?|(,*)/g, '')"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="연간 사용료">
              <a-input-number
                v-model:value="form.usageFee.yearly"
                :min="0"
                :formatter="value => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="value => value.replace(/₩\s?|(,*)/g, '')"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="일회성 사용료">
              <a-input-number
                v-model:value="form.usageFee.oneTime"
                :min="0"
                :formatter="value => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="value => value.replace(/₩\s?|(,*)/g, '')"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="건당 사용료">
              <a-input-number
                v-model:value="form.usageFee.perTransaction"
                :min="0"
                :formatter="value => `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                :parser="value => value.replace(/₩\s?|(,*)/g, '')"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 설명 -->
        <a-form-item label="설명">
          <a-textarea
            v-model:value="form.description"
            :rows="3"
            placeholder="제품에 대한 설명을 입력하세요"
          />
        </a-form-item>
      </a-form>

      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button
          type="primary"
          :loading="loading"
          @click="handleSubmit"
          class="yellow-button"
        >
          저장
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from 'ant-design-vue';
import { systemProductApi } from '../api';
import { SystemProductTypeIcon } from '../enums';

export default {
  name: 'ProductEditModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: null,
    },
  },
  emits: ['close', 'updated'],
  data() {
    return {
      loading: false,
      form: {
        installationFee: 0,
        usageFee: {
          monthly: 0,
          yearly: 0,
          oneTime: 0,
          perTransaction: 0,
        },
        description: '',
      },
    };
  },
  computed: {
    productIcon() {
      if (!this.product?.type) return 'fa-box';
      return SystemProductTypeIcon[this.product.type] || 'fa-box';
    },
  },
  watch: {
    product: {
      handler(newProduct) {
        if (newProduct) {
          this.form = {
            installationFee: newProduct.installationFee || 0,
            usageFee: {
              monthly: newProduct.usageFee?.monthly || 0,
              yearly: newProduct.usageFee?.yearly || 0,
              oneTime: newProduct.usageFee?.oneTime || 0,
              perTransaction: newProduct.usageFee?.perTransaction || 0,
            },
            description: newProduct.description || '',
          };
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleCancel() {
      this.$emit('close');
    },
    async handleSubmit() {
      if (!this.product?.id) {
        message.warning('제품 정보가 없습니다.');
        return;
      }

      this.loading = true;
      try {
        await systemProductApi.update(this.product.id, this.form);
        message.success('제품 설정이 저장되었습니다.');
        this.$emit('updated');
        this.$emit('close');
      } catch (error) {
        console.error('제품 수정 실패:', error);
        message.error('제품 설정 저장에 실패했습니다.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;

  i {
    color: #faad14;
  }
}

.modal-content {
  padding: 24px 24px 16px 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.yellow-button {
  background-color: #ffe414 !important;
  border-color: #ffe414 !important;
  color: #17191c !important;

  &:hover {
    background-color: #ffd900 !important;
    border-color: #ffd900 !important;
  }
}
</style>
